import { ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES } from 'accounts/constants/home-disclosures';

/**
 * Scrolls to the first disclosure with a service error
 * @param errors Array of service errors with errorCode and message
 * @param disclosureRefs Ref object containing references to disclosure elements
 */
export const scrollToFirstServiceError = (
  errors: Array<{ errorCode: string; message: string }>,
  disclosureRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
) => {
  if (!errors || errors.length === 0) return;

  // Guarded by the length check above.
  const firstErrorCode = errors[0]!.errorCode;

  // Try to find the element by ref first (for always visible disclosures)
  let targetElement = disclosureRefs.current[firstErrorCode];

  // If not found in refs, try to find it by field name in selectable disclosures
  if (!targetElement) {
    // Try different field name patterns for selectable disclosures
    const possibleSelectors = [
      `[name="disclosures.types.${firstErrorCode}.selected"]`,
      `[name="disclosures.types.${firstErrorCode}"]`,
      `input[name*="${firstErrorCode}"]`,
      `[id*="${firstErrorCode}"]`,
    ];

    let fieldElement = null;
    for (const selector of possibleSelectors) {
      fieldElement = document.querySelector(selector);

      if (fieldElement) break;
    }

    if (fieldElement) {
      // Find the closest parent container that represents the disclosure item
      // Look for common parent containers
      // `closest` returns `Element` unless told what it is looking for, and
      // `targetElement` is a `HTMLDivElement` ref. Every selector here matches a
      // `div`, which is also what the last branch below already asserted.
      targetElement =
        fieldElement.closest<HTMLDivElement>('[class*="item"]') ||
        fieldElement.closest<HTMLDivElement>('.border') ||
        fieldElement.closest<HTMLDivElement>('[class*="selectable"]') ||
        fieldElement.closest<HTMLDivElement>('[class*="panel"]') ||
        fieldElement.closest<HTMLDivElement>('div[class*="mb-"]');
    }
  }

  // If still not found, try to find by looking for text content
  if (!targetElement) {
    // Get the disclosure title from our constants
    const disclosureItem = ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES.find(
      (item) => item.name === firstErrorCode,
    );
    if (disclosureItem) {
      // Try to find by text content
      const textElements = Array.from(document.querySelectorAll('*')).filter(
        (el) =>
          el.textContent &&
          el.textContent.includes(disclosureItem.title.substring(0, 30)),
      );
      if (textElements.length > 0) {
        targetElement =
          textElements[0]!.closest<HTMLDivElement>('.border') ||
          textElements[0]!.closest<HTMLDivElement>('[class*="item"]') ||
          (textElements[0] as HTMLDivElement);
      }
    }
  }

  if (targetElement) {
    // Scroll to the disclosure with error
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  } else {
    // Fallback: scroll to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

/**
 * Scrolls to the first input field with a validation error
 */
export const scrollToFirstValidationError = () => {
  // Find the first input field with a validation error
  const errorInputs = document.querySelectorAll(
    'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]',
  );

  if (errorInputs.length > 0) {
    const firstErrorInput = errorInputs[0]!;

    // Scroll to the first input with an error
    firstErrorInput.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  } else {
    // Fallback: try to find any element with error styling
    const errorElements = document.querySelectorAll(
      '[class*="error"], [class*="invalid"]',
    );
    if (errorElements.length > 0) {
      const firstErrorElement = errorElements[0]!;

      firstErrorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }
};

/**
 * Transforms service response data back to form structure for initial values
 * @param serviceResponse The response from the disclosures service
 * @returns Transformed data ready for form initialization
 */
export const transformServiceResponseToFormValues = (serviceResponse: any) => {
  if (!serviceResponse?.disclosures?.types) {
    return serviceResponse;
  }

  const transformedResponse = structuredClone(serviceResponse);
  const { disclosures } = transformedResponse;

  Object.keys(disclosures.types).forEach((type) => {
    const disclosure = disclosures.types[type];

    // Rebuild each DC housing-code violation row's notice file from the
    // disclosure documents, matched by fileUniqueId, so previously-uploaded
    // notices reappear per row. NOTE: the exact multi-file documents payload
    // shape is pending backend confirmation (DC disclosures handoff, step 4).
    if (type === 'HousingCodeViolations') {
      const documents = [
        ...(Array.isArray(disclosure.documents)
          ? disclosure.documents.flatMap((doc: any) => doc?.files ?? doc)
          : []),
        ...(Array.isArray(disclosure.documentsToUpsert)
          ? disclosure.documentsToUpsert
          : []),
      ].filter(Boolean);

      if (Array.isArray(disclosure.housingCodeViolations)) {
        disclosure.housingCodeViolations = disclosure.housingCodeViolations.map(
          (row: any) => {
            if (!row?.fileUniqueId) {
              return row;
            }

            const matched = documents.find(
              (doc) => doc?.uniqueId === row.fileUniqueId,
            );

            return matched
              ? {
                  ...row,
                  file: { availableMedia: [matched], stagingMedia: [] },
                }
              : row;
          },
        );
      }

      // Skip the generic documentsToUpsert→files bundling below: housing-code
      // files are per row, not a single disclosure-level upload.
      delete disclosure.documentsToUpsert;

      return;
    }

    // Transform LeadGovernmentPendingActionsAndNotices back to form structure
    if (
      type === 'LeadGovernmentPendingActionsAndNotices' &&
      disclosure.leadGovernmentPendingActionsAndNoticesOptions
    ) {
      const options = disclosure.leadGovernmentPendingActionsAndNoticesOptions;

      disclosure.ViolationNotice = options.includes('ViolationNotice');
      disclosure.LeadBasedPaintHazardNotice = options.includes(
        'LeadBasedPaintHazardNotice',
      );
      disclosure.EliminateLeadBasedPaintHazardsOrder = options.includes(
        'EliminateLeadBasedPaintHazardsOrder',
      );
      disclosure.CivilFinesOrFees = options.includes('CivilFinesOrFees');
      disclosure.Other = options.includes('Other');

      // Keep the Other text value if it exists
      if (
        disclosure.Other &&
        disclosure.leadGovernmentPendingActionsAndNoticesOther
      ) {
        disclosure.NoticeOrderTypeDetails =
          disclosure.leadGovernmentPendingActionsAndNoticesOther;
      }

      // Clean up service response structure
      delete disclosure.leadGovernmentPendingActionsAndNoticesOptions;
      delete disclosure.leadGovernmentPendingActionsAndNoticesOther;
    }

    // Transform LeadServiceLines back to form structure
    if (
      type === 'LeadServiceLines' &&
      disclosure.leadServiceLinesLocationOptions
    ) {
      const options = disclosure.leadServiceLinesLocationOptions;

      disclosure.publicSpace = options.includes('PublicSpace');
      disclosure.privateProperty = options.includes('PrivateProperty');

      // Clean up service response structure
      delete disclosure.leadServiceLinesLocationOptions;
    }

    // Transform documentsToUpsert back to files structure if needed
    if (disclosure.documentsToUpsert) {
      disclosure.files = {
        availableMedia: disclosure.documentsToUpsert,
        stagingMedia: [],
      };
      delete disclosure.documentsToUpsert;
    }
  });

  return transformedResponse;
};

/**
 * Transforms disclosure structure for API submission
 * @param transformedValues The form values to transform
 */
export const transformDisclosureStructure = (transformedValues: any) => {
  if (!transformedValues.disclosures || !transformedValues.disclosures.types) {
    return;
  }

  Object.keys(transformedValues.disclosures.types).forEach((type) => {
    const disclosure = transformedValues.disclosures.types[type];

    if (disclosure.selected === false) {
      delete transformedValues.disclosures.types[type];
      return;
    }

    // Housing-code violation notice files are uploaded per row (via
    // updateDisclosuresFilesIfNecessary, which sets each row's fileUniqueId and
    // the disclosure documentsToUpsert). Strip any leftover per-row media object
    // so it never leaks into the PUT payload.
    if (
      type === 'HousingCodeViolations' &&
      Array.isArray(disclosure.housingCodeViolations)
    ) {
      disclosure.housingCodeViolations = disclosure.housingCodeViolations.map(
        (row: any) => {
          if (row && typeof row === 'object' && 'file' in row) {
            const { file, ...rest } = row;

            return rest;
          }

          return row;
        },
      );
    }

    if (type === 'LeadGovernmentPendingActionsAndNotices') {
      const selectedOptions = [];

      if (disclosure.ViolationNotice) selectedOptions.push('ViolationNotice');
      if (disclosure.LeadBasedPaintHazardNotice)
        selectedOptions.push('LeadBasedPaintHazardNotice');
      if (disclosure.EliminateLeadBasedPaintHazardsOrder)
        selectedOptions.push('EliminateLeadBasedPaintHazardsOrder');
      if (disclosure.CivilFinesOrFees) selectedOptions.push('CivilFinesOrFees');
      if (disclosure.Other) {
        selectedOptions.push('Other');
        // Store the Other text value in the correct field for the API
        if (disclosure.NoticeOrderTypeDetails) {
          disclosure.leadGovernmentPendingActionsAndNoticesOther =
            disclosure.NoticeOrderTypeDetails;
        }
      }

      disclosure.leadGovernmentPendingActionsAndNoticesOptions =
        selectedOptions;

      // Clean up old structure
      delete disclosure.ViolationNotice;
      delete disclosure.LeadBasedPaintHazardNotice;
      delete disclosure.EliminateLeadBasedPaintHazardsOrder;
      delete disclosure.CivilFinesOrFees;
      delete disclosure.Other;
      delete disclosure.NoticeOrderTypeDetails;
    }

    // Transform LeadServiceLines structure
    if (type === 'LeadServiceLines') {
      const selectedOptions = [];

      if (disclosure.publicSpace) selectedOptions.push('PublicSpace');
      if (disclosure.privateProperty) selectedOptions.push('PrivateProperty');

      disclosure.leadServiceLinesLocationOptions = selectedOptions;

      // Clean up old structure TOOD: remove this once we have the new structure in the backend
      delete disclosure.publicSpace;
      delete disclosure.privateProperty;
    }

    // Transform files.availableMedia to documentsToUpsert format
    if (disclosure.files?.availableMedia?.length > 0) {
      disclosure.documentsToUpsert = disclosure.files.availableMedia.map(
        (file: any) => ({
          uniqueId: file.uniqueId,
          name: file.name,
          contentType: file.contentType,
        }),
      );
    }

    // Remove properties that shouldn't be sent in PUT requests
    delete disclosure.documents;
    delete disclosure.files;
  });
};

/**
 * Cleans up form values for disclosures that are no longer visible
 * @param currentVisibleDisclosures Array of currently visible disclosure names
 * @param previousVisibleDisclosures Array of previously visible disclosure names
 * @param form The form instance to clean up
 */
export const cleanupHiddenDisclosures = (
  currentVisibleDisclosures: string[],
  previousVisibleDisclosures: string[],
  form: any,
) => {
  const hiddenDisclosures = previousVisibleDisclosures.filter(
    (disclosureName) => !currentVisibleDisclosures.includes(disclosureName),
  );

  if (hiddenDisclosures.length > 0) {
    const currentValues = form.getState().values;

    hiddenDisclosures.forEach((disclosureName) => {
      // Remove the disclosure from form values
      if (currentValues.disclosures?.types?.[disclosureName]) {
        form.change(`disclosures.types.${disclosureName}`, undefined);
      }
    });
  }
};
